import React, { useState, useCallback, useRef, MouseEvent as ReactMouseEvent, useEffect } from "react";

export const PanelDragContext = React.createContext<boolean>(false);

interface ResizablePanelsProps {
    children: React.ReactNode[];
    className?: string;
    defaultSizes?: number[];
    storageKey?: string;
    minSize?: number;
    onResize?: (newSizes: number[]) => void;
    isPlaying?: boolean;
    setIsPlaying?: (playing: boolean) => void;
}

export const ResizablePanels: React.FC<ResizablePanelsProps> = ({
    children,
    className = "",
    defaultSizes,
    storageKey,
    minSize = 0.1,
    onResize,
    isPlaying,
    setIsPlaying,
}) => {
    const [sizes, setSizes] = useState<number[]>(() => {
        if (storageKey) {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsedSizes = JSON.parse(saved);
                if (
                    parsedSizes.length === React.Children.count(children) &&
                    parsedSizes.reduce((a: number, b: number) => a + b, 0) === 1
                ) {
                    return parsedSizes;
                }
            }
        }
        return (
            defaultSizes ||
            Array(React.Children.count(children)).fill(1 / React.Children.count(children))
        );
    });

    useEffect(() => {
        if (defaultSizes && defaultSizes.length === React.Children.count(children)) {
            setSizes(defaultSizes);
        } else {
            setSizes(
                Array(React.Children.count(children)).fill(1 / React.Children.count(children))
            );
        }
    }, [defaultSizes, children]);

    const [isDragging, setIsDragging] = useState<number | null>(null);
    const [startSizes, setStartSizes] = useState<number[]>([]);
    const [startX, setStartX] = useState<number>(0);
    const wasPlayingOnDragStart = useRef<boolean>(false);

    const handleMouseDown = useCallback(
        (index: number, e: ReactMouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragging(index);
            setStartSizes([...sizes]);
            setStartX(e.clientX);
            // Pause video while dragging, remembering whether it was playing
            wasPlayingOnDragStart.current = Boolean(isPlaying);
            if (isPlaying && setIsPlaying) {
                setIsPlaying(false);
            }
        },
        [sizes, isPlaying, setIsPlaying]
    );

    React.useEffect(() => {
        const handleMouseMove = (e: globalThis.MouseEvent) => {
            if (isDragging === null) return;

            const container = document.getElementById("resizable-panels-container");
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const deltaX = e.clientX - startX;
            const deltaPercentage = deltaX / containerRect.width;

            setSizes(prevSizes => {
                const newSizes = [...prevSizes];
                newSizes[isDragging] = startSizes[isDragging] + deltaPercentage;
                newSizes[isDragging + 1] = startSizes[isDragging + 1] - deltaPercentage;

                if (newSizes[isDragging] < minSize || newSizes[isDragging + 1] < minSize) {
                    return prevSizes;
                }

                return newSizes;
            });
        };

        const handleMouseUp = () => {
            if (isDragging !== null) {
                setIsDragging(null);
                if (storageKey) {
                    localStorage.setItem(storageKey, JSON.stringify(sizes));
                }
                if (onResize) {
                    onResize(sizes);
                }
                // Restore playback only if the video was playing before the drag
                if (setIsPlaying && wasPlayingOnDragStart.current) {
                    setIsPlaying(true);
                }
            }
        };

        if (isDragging !== null) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, startX, startSizes, sizes, storageKey, minSize, onResize, setIsPlaying]);
    const childCount = React.Children.count(children);
    if (childCount < 2) {
        return (
            <div
                id="resizable-panels-container"
                className={`flex w-full h-full overflow-hidden ${className}`}
            >
                {React.Children.map(children, (child, i) => {
                    const childKey =
                        React.isValidElement(child) && child.key != null ? child.key : i;
                    return (
                        <div
                            key={childKey}
                            className="relative overflow-hidden"
                            style={{
                                width: `${(sizes[i] ?? 1) * 100}%`,
                                transition: "width 0.25s ease-out",
                            }}
                        >
                            <PanelDragContext.Provider value={false}>
                                {child}
                            </PanelDragContext.Provider>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div
            id="resizable-panels-container"
            className={`flex w-full h-full overflow-hidden ${className}`}
        >
            {React.Children.map(children, (child, i) => {
                const childKey =
                    React.isValidElement(child) && child.key != null ? child.key : i;
                return (
                <div
                    key={childKey}
                    className="relative overflow-hidden"
                    style={{
                        width: `${sizes[i] * 100}%`,
                        transition: isDragging === null ? "width 0.25s ease-out" : "none",
                    }}
                >
                    <PanelDragContext.Provider
                        value={isDragging !== null && (i === isDragging || i === isDragging + 1)}
                    >
                        {child}
                    </PanelDragContext.Provider>
                    {i < childCount - 1 && sizes[i] > 0 && sizes[i + 1] > 0 && (
                        <div
                            className="absolute top-0 right-0 w-3 h-full cursor-col-resize z-50"
                            onMouseDown={e => handleMouseDown(i, e)}
                        />
                    )}
                </div>
                );
            })}
        </div>
    );
};

export default ResizablePanels;
